import { YhcEstimateFormFrontService } from './YhcEstimateFormFrontService'
import {
  ModelKazaiOptionServicesOneSelection,
  ModelYhcKazaiTypesFullRelation,
} from '../../fireModels/yhcDataTypes'
import { calcSizeRank, YhcSizeRank } from '../../constants/sizeRanks'
import { reactive } from 'vue'

/**
 * 家財ごとの選択されたオプションを保持
 */
export type YhcEFInputSelectedOptionsByServiceId = {
  [serviceId: number]: ModelKazaiOptionServicesOneSelection[]
}

/**
 * ユーザがオプションなどを追加して 生成される 家財のデータ
 * 主に `YhcEFKazaiDetailInput.vue` で使用, 生成され YhcEFUserInputDataHandler にて保持される
 * 家財単体での料金計算などを行う
 *
 * ## 責任範囲
 * - 選択された家財種別のIDを保持する
 * - サイズを保持する
 * - 選択されたオプションを保持する
 * - エリアによる選択オプションの表示非表示を判断する
 * - 数量を保持する
 * - 自身の配送基本料金を算出する
 * - 自身のオプション料金を算出する
 * - 自身の小計金額を算出する
 *
 * ## 責任範囲外
 * - 他の家財の情報
 * - 郵便番号や都道府県などの配送先情報
 *
 */
export class YhcEFInputOneKazaiTypeDataObject {
  public id: string // 追加時に random 生成, unique を振る
  // 家財のID
  public kazaiTypeId: number = null
  public size_height: number = null
  public size_width: number = null
  public size_depth: number = null
  public selectedOptionsByServiceId: YhcEFInputSelectedOptionsByServiceId = {} // この家財に選択されたオプション
  public readonly quantity: number = 1 // 一旦固定で... 後々 必要になるかもしれない
  public readonly $efs: YhcEstimateFormFrontService

  /**
   * "その他" の場合に ユーザが入力する家財種別の名前
   */
  public userInputName: string = ''
  constructor(
    $efs: YhcEstimateFormFrontService, // 親サービスへの参照 (循環参照になる)
    initData: Partial<YhcEFInputOneKazaiTypeDataObject> = null,
  ) {
    // JSON に変換するときに循環参照 Error になるので、 non-enumerable にする
    Object.defineProperty(this, '$efs', {
      value: $efs,
      enumerable: false,
    })
    if (initData) {
      Object.assign(this, initData)
    }
    // ID がなければ生成
    if (!initData?.id) {
      // @ts-ignore
      this.id = $core.$utils.generateUUIDV4()
    }
  }

  get isAddable(): boolean {
    if (!this.kazaiTypeId) {
      return false
    }
    if (this.isUserInputNameRequired && !this.userInputName) {
      return false
    }
    if (this.hasError) {
      return false
    }
    return true
  }

  get kazaiType(): ModelYhcKazaiTypesFullRelation | null {
    return this.$efs.allKazaiTypesById[this.kazaiTypeId]
  }

  get pickupAreaFlag(): string {
    return this.$efs.$userInputData.zip_from_yhc_master?.deliveryAreaFlag
  }

  get deliveryAreaFlag(): string {
    return this.$efs.$userInputData.zip_to_yhc_master?.deliveryAreaFlag
  }

  get availableServiceOptionIdsBasedOnKazaiType(): number[] {
    return this.kazaiType.serviceOptions.map((option) => option.service.id)
  }

  get sizeTotal(): number {
    if (!this.size_height || !this.size_width || !this.size_depth) {
      return 0
    }
    return this.size_height + this.size_width + this.size_depth
  }
  get sizeRank(): YhcSizeRank | null {
    if (!this.sizeTotal) {
      return null
    }
    return calcSizeRank(this.sizeTotal)
  }

  /**
   * サイズランクに基づく基本料金
   * - サイズランクがない場合は null
   * - エラー (対応エリア外 or サイズ超過など) がある場合は null
   */
  get basePriceBySizeRank(): number | null {
    if (!this.sizeRank) {
      return null
    }
    if (this.hasError) {
      return null
    }
    return (
      this.$efs.$userInputData.pref_from_to_info_master?.[`price_${this.sizeRank}`] || 0
    )
  }

  /**
   * selectedOptionsByServiceId をお預かり先エリア、お届け先エリアに基づいてフィルタリング
   * - オプション利用不可エリア（特殊家財不能、家財不能エリア）の場合に選択済みのオプションが含まれていたら除外する挙動
   */
  get availableAreaSelectedOptionsByServiceId(): {
    [serviceId: number]: ModelKazaiOptionServicesOneSelection[]
  } {
    if (!this.kazaiType) {
      return {}
    }
    return Object.keys(this.selectedOptionsByServiceId).reduce((acc, serviceId) => {
      const selectedOptions = this.selectedOptionsByServiceId[serviceId]
      const filteredSelectedOptions = selectedOptions.filter((selectedOption) => {
        if (selectedOption.areaRestrictionType === 'fromArea') {
          return selectedOption.availableDeliveryArea.includes(this.pickupAreaFlag)
        } else if (selectedOption.areaRestrictionType === 'toArea') {
          return selectedOption.availableDeliveryArea.includes(this.deliveryAreaFlag)
        } else {
          return (
            selectedOption.availableDeliveryArea.includes(this.pickupAreaFlag) &&
            selectedOption.availableDeliveryArea.includes(this.deliveryAreaFlag)
          )
        }
      })
      if (filteredSelectedOptions.length > 0) {
        // 選択されたオプションが1つ以上ある場合のみserviceIdとして追加
        acc[serviceId] = filteredSelectedOptions
      }
      return acc
    }, {})
  }

  /**
   * kazaiType が 保持する 正しいオプションのみ を返す
   */
  get availableSelectedOptionsByServiceId(): {
    [serviceId: number]: ModelKazaiOptionServicesOneSelection[]
  } {
    if (!this.kazaiType) {
      return {}
    }

    const availableServiceOptionIdsBasedOnKazaiType =
      this.availableServiceOptionIdsBasedOnKazaiType
    return Object.keys(this.availableAreaSelectedOptionsByServiceId).reduce(
      (acc, serviceId) => {
        if (availableServiceOptionIdsBasedOnKazaiType.includes(Number(serviceId))) {
          acc[serviceId] = this.availableAreaSelectedOptionsByServiceId[serviceId]
        }
        return acc
      },
      {},
    )
  }

  get optionTotalPrice(): number {
    return Object.keys(this.availableSelectedOptionsByServiceId).reduce(
      (acc, serviceId) => {
        const selectedOptions = this.availableSelectedOptionsByServiceId[serviceId]
        return (
          acc +
          selectedOptions.reduce((acc, selectedOption) => {
            return acc + selectedOption.price
          }, 0)
        )
      },
      0,
    )
  }

  get totalPrice(): number {
    if (!this.basePriceBySizeRank) {
      return 0
    }
    return (this.basePriceBySizeRank + this.optionTotalPrice) * this.quantity
  }

  /**
   * 編集用などで clone するためのメソッド
   */
  clone(): YhcEFInputOneKazaiTypeDataObject {
    const cloned = reactive(new YhcEFInputOneKazaiTypeDataObject(this.$efs))
    cloned.kazaiTypeId = this.kazaiTypeId
    cloned.size_height = this.size_height
    cloned.size_width = this.size_width
    cloned.size_depth = this.size_depth
    cloned.userInputName = this.userInputName
    cloned.selectedOptionsByServiceId = JSON.parse(
      JSON.stringify(this.selectedOptionsByServiceId),
    )
    return cloned as YhcEFInputOneKazaiTypeDataObject
  }

  /**
   * ユーザが家財の名称を 入力することが必須であるかどうか
   */
  get isUserInputNameRequired(): boolean {
    return this.kazaiType.displayNameFormEnabled
  }

  /**
   * 大型家財 (YHCさんの言葉で "特殊家財") であるかどうか (お届け日数に影響)
   *
   * ## 大型家財である Logic
   * - サイズランク f 以上 (3辺合計 350cm を超える)
   * - どこか 1辺 が 180cm を超える
   * - 横・奥行のどちらかが 100cm を超える
   * ※ 天地条件は 結局利用しない（上記条件でカバーできるため）
   *
   */
  get isLargeSize(): boolean {
    if (['f', 'g'].indexOf(this.sizeRank) >= 0) {
      return true
    }
    if (this.size_height > 180 || this.size_width > 180 || this.size_depth > 180) {
      return true
    }
    if (this.size_depth > 100 || this.size_width > 100) {
      return true
    }
    return false
  }

  /**
   * Validations
   */
  /**
   * Validation:
   * - サイズは 合計450cm 以下であること
   * - かつ 1辺の長さが 250cm 以下であること
   * - 天地が定められたものは高さが200㎝以下
   * - だたし、サイズ上限なし が true であれば 無視
   */
  get sizeErrorMessage(): string {
    if (!this.size_height || !this.size_width || !this.size_depth) {
      return 'サイズを入力してください。'
    }
    if (this.$efs.$userInputData.canAcceptLargeSizeKazai === false && this.isLargeSize) {
      return 'ご指定のエリアでは お取り扱いできないサイズの家財です。 (3辺合計 350cm 以下, 幅・奥行のどちらも 100cm 以下, 高さ 180cm 以下 の条件をすべて満たす必要があります。)'
    }
    if (this.kazaiType.noSizeLimit) {
      return ''
    }
    if (this.sizeTotal > 450) {
      return '3辺合計 450cm を超える家財は運べません。'
    }
    if (this.size_height > 250 || this.size_width > 250 || this.size_depth > 250) {
      return '最長辺の長さ 250cm を超える家財は運べません。'
    }
    if (this.kazaiType.hasOriented && this.size_height > 200) {
      return '天地が定められた家財の高さは200cm 以下である必要があります。'
    }
    return ''
  }

  /**
   * エラーがあるかどうか (サイズエラー or 配送エリアエラー)
   */
  get hasError(): boolean {
    if (this.sizeErrorMessage) {
      return true
    }
    // Notice: this.$efs.$userInputData.hasError を使うと infinite loop になるので注意
    if (this.$efs.$userInputData.hasPickupAndDeliveryError) {
      return true
    }
    return false
  }

  /**
   * 特殊家財`isLargeSize` であり、特殊家財可能エリアだった場合、エラーとはしないが通常よりも日数がかかることを注釈入れる
   */
  get largeSizeNoticeMessage(): string {
    if (this.sizeErrorMessage) {
      // サイズエラーがある場合は、通常のエラーメッセージを優先
      return ''
    }
    if (this.$efs.$userInputData.canAcceptLargeSizeKazai === true && this.isLargeSize) {
      return 'こちらの家財は高さ 180cm を超える又は、幅、奥行のどちらかが 100cm を超えるため、通常の配送よりも日数をいただきます。'
    }
    return ''
  }
}
